class ManmadeCustomOption extends HTMLElement {
    static PRODUCT_GROUP_SELECTOR = '[data-product-group]';
    static SELECTED_VARIANT_SELECTOR = 'variant-selects [data-selected-variant]';

    productGroup = {
        items: [],
        itemsByProductId: new Map()
    };

    productMap = Object.create(null);
    variantMap = Object.create(null);

    constructor() {
        super();

        this.buttons = [];
        this._onClick = this.onClick.bind(this);
        this._swapAbort = null;
    }

    connectedCallback() {
        this.buttons = Array.from(this.querySelectorAll('button'));

        this.loadProductData();
        this.syncPressedState();
        this.syncSwatchLabel();

        for (const button of this.buttons) {
            button.addEventListener('click', this._onClick);
        }
    }

    disconnectedCallback() {
        this._swapAbort?.abort();
        this._swapAbort = null;

        if (this.buttons && this._onClick) {
            for (let button of this.buttons) {
                button.removeEventListener('click', this._onClick);
            }
        }
    }
    
    onClick(event) {
        const button = event.target?.closest?.('button');
        if (!button || !this.contains(button)) return;

        const stateChange = this.getStateChangeFromButton(button);
        if (!stateChange) return;

        const currentState = this.getCurrentState();

        if (currentState[stateChange.key] === stateChange.value) {
            return;
        }

        const nextState = {
            ...currentState,
            [stateChange.key]: stateChange.value
        };

        const targetProduct = this.resolveProductFromState(nextState);
        if (!targetProduct) {
            console.warn('[CustomOptions] No product found for state:', nextState);
            return;
        }

        // Optimistic UI: reflect the new selection synchronously so the click
        // registers before the network round-trip completes. Otherwise the
        // aria-pressed / swatch label only flip when the swapped-in HTML
        // lands, which reads as a delayed layout shift.
        this.applyOptimisticState(nextState);

        this.swapToProduct(targetProduct, nextState);
    }

    applyOptimisticState(nextState) {
        for (const button of this.buttons) {
            const stateChange = this.getStateChangeFromButton(button);
            const isPressed = stateChange
                ? nextState[stateChange.key] === stateChange.value
                : false;

            button.setAttribute('aria-pressed', isPressed ? 'true' : 'false');
        }

        this.syncSwatchLabel();
    }
    
    getCurrentState() {
        const selectedVariant = this.getSelectedVariant();
        const selectedVariantId = this.normalizeId(selectedVariant?.id);
        const variant = selectedVariantId ? this.variantMap[selectedVariantId] : null;

        const currentProductId = this.normalizeId(
            variant?.productId ??
            selectedVariant?.product_id ??
            selectedVariant?.product?.id
        );

        const variantState = this.normalizeStateObject(variant?.options);
        const productState = this.getProductState(currentProductId);

        return {
            ...variantState,
            ...productState
        };
    }

    getProductState(productId) {
        const normalizedProductId = this.normalizeId(productId);
        if (!normalizedProductId) return {};

        return this.productGroup.itemsByProductId.get(normalizedProductId)?.state ?? {};
    }

    getSelectedVariant() {
        return this.readJsonScript(
            ManmadeCustomOption.SELECTED_VARIANT_SELECTOR,
            'selected variant'
        );
    }

    getStateChangeFromButton(button) {
        const key = this.normalizeStateKey(button.dataset.stateKey);
        const value = this.normalizeStateValue(button.dataset.stateValue);

        if (!key || !value) return null;

        return { key, value };
    }

    syncPressedState() {
        if (this.buttons.length == 1) return;

        const currentState = this.getCurrentState();

        for (const button of this.buttons) {
            const stateChange = this.getStateChangeFromButton(button);
            const isPressed = stateChange
                ? currentState[stateChange.key] === stateChange.value
                : false;

            button.setAttribute('aria-pressed', isPressed ? 'true' : 'false');
        }
    }

    syncSwatchLabel() {
        if (!this.dataset.isSwatch) return;

        const label = this.parentElement?.querySelector('[data-selected-value]');
        const selectedButton = this.buttons.find(button => {
            return button.getAttribute('aria-pressed') === 'true';
        });
        const selectedValue = selectedButton?.getAttribute('aria-label')?.trim();

        if (label && selectedValue) {
            label.textContent = selectedValue;
        }
    }

    loadProductData() {
        const data = this.readJsonScript(
            ManmadeCustomOption.PRODUCT_GROUP_SELECTOR,
            'product group'
        );

        this.productGroup = this.normalizeProductGroup(data?.items);
        this.productMap = this.normalizeProductMap(data?.productMap);
        this.variantMap = this.normalizeVariantMap(data?.variantMap);
    }

    normalizeId(value) {
        if (value == null) return null;

        const str = String(value).trim();
        if (!str) return null;

        return str.includes('/') ? str.split('/').pop() : str;
    }

    resolveProductFromState(state) {
        const normalizedState = this.normalizeStateObject(state);

        const candidates = this.productGroup.items.filter(item => {
            return this.stateContains(normalizedState, item.state);
        });

        if (!candidates.length) return null;

        candidates.sort((a, b) => {
            return this.getStateSpecificity(b.state) - this.getStateSpecificity(a.state);
        });

        return candidates[0];
    }

    resolveVariantId(state, productId) {
        const query = this.buildProductOptionQuery(state, productId);
        const candidates = this.getVariantsForProduct(productId).filter(variant => {
            return this.variantMatchesQuery(variant, query);
        });

        if (!candidates.length) return null;

        candidates.sort((a, b) => a.position - b.position);

        return candidates[0].variantId;
    }

    buildProductOptionQuery(state, productId) {
        const query = {};
        const normalizedState = this.normalizeStateObject(state);
        const targetOptions = this.productMap[this.normalizeId(productId)];

        if (!targetOptions) return query;

        for (const [optionName, allowedValues] of Object.entries(targetOptions)) {
            const stateKey = this.normalizeStateKey(optionName);
            const stateValue = normalizedState[stateKey];

            if (!stateValue) continue;

            const matchingValue = this.findMatchingOptionValue(allowedValues, stateValue);

            if (matchingValue != null) {
                query[optionName] = matchingValue;
            }
        }

        return query;
    }

    getVariantsForProduct(productId) {
        const normalizedProductId = this.normalizeId(productId);

        return Object.values(this.variantMap).filter(variant => {
            return variant.productId === normalizedProductId;
        });
    }

    variantMatchesQuery(variant, query) {
        return Object.entries(query).every(([optionName, optionValue]) => {
            const key = this.normalizeStateKey(optionName);
            const value = this.normalizeStateValue(optionValue);

            return variant.normalizedOptions[key] === value;
        });
    }

    findMatchingOptionValue(values, normalizedValue) {
        if (!Array.isArray(values)) return null;

        return values.find(value => {
            return this.normalizeStateValue(value) === normalizedValue;
        });
    }

    handleSwapProduct(url, variantId) {
        return (html) => {
            this.updateURL(url, variantId);

            // Section-scoped responses don't include <head>, so the response
            // is just the section markup. When we get the full page back
            // (fallback path), the section still lives inside <main>. Either
            // way we only need to swap <manmade-product-info>, not #MainContent
            // — that keeps the header/breadcrumbs/etc. from being torn down
            // and dramatically cuts CLS.
            const currentProductInfo = document.querySelector('manmade-product-info');
            const newProductInfo = html.querySelector('manmade-product-info');

            if (!currentProductInfo || !newProductInfo) {
                console.warn('[CustomOptions] manmade-product-info not found during product swap.');
                window.location.href = this.buildRequestUrlWithParams(url, variantId);
                return;
            }

            HTMLUpdateUtility.viewTransition(
                currentProductInfo,
                newProductInfo,
                [],
                [() => this.initializeDynamicWidgets()]
            );
        };
    }

    initializeDynamicWidgets() {
        window?.Shopify?.PaymentButton?.init();
        window?.ProductModel?.loadShopifyXR();
    }

    normalizePosition(position) {
        const value = Number(position);
        return Number.isFinite(value) ? value : 0;
    }

    normalizeProductGroup(items = []) {
        const normalizedItems = Array.isArray(items)
            ? items
                .map(item => this.normalizeProductGroupItem(item))
                .filter(Boolean)
            : [];

        return {
            items: normalizedItems,
            itemsByProductId: new Map(
                normalizedItems.map(item => [item.productId, item])
            )
        };
    }

    normalizeProductGroupItem(item) {
        const productId = this.normalizeId(item?.productId);

        if (!productId) return null;

        return {
            ...item,
            productId,
            state: this.normalizeStateObject(item?.state)
        };
    }

    normalizeProductMap(productMap = {}) {
        const normalized = Object.create(null);

        for (const [productId, options] of Object.entries(productMap ?? {})) {
            const normalizedProductId = this.normalizeId(productId);

            if (!normalizedProductId) continue;

            normalized[normalizedProductId] = options ?? Object.create(null);
        }

        return normalized;
    }

    normalizeVariantMap(variantMap = {}) {
        const normalized = Object.create(null);

        for (const [mapKey, variant] of Object.entries(variantMap ?? {})) {
            const variantId = this.normalizeId(variant?.variantId ?? mapKey);
            const productId = this.normalizeId(variant?.productId);

            if (!variantId || !productId) continue;

            normalized[variantId] = {
                ...variant,
                variantId,
                productId,
                position: this.normalizePosition(variant?.position),
                options: variant?.options ?? Object.create(null),
                normalizedOptions: this.normalizeStateObject(variant?.options)
            };
        }

        return normalized;
    }

    normalizeStateObject(state = {}) {
        const normalized = Object.create(null);

        for (const [key, value] of Object.entries(state ?? {})) {
            const normalizedKey = this.normalizeStateKey(key);
            const normalizedValue = this.normalizeStateValue(value);

            if (!normalizedKey || !normalizedValue) continue;

            normalized[normalizedKey] = normalizedValue;
        }

        return normalized;
    }

    normalizeStateKey(value) {
        return String(value ?? '')
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '_');
    }

    normalizeStateValue(value) {
        return String(value ?? '')
            .trim()
            .toLowerCase();
    }

    readJsonScript(selector, label) {
        const root = this.closest('section') ?? document;
        const el = root.querySelector(selector) ?? document.querySelector(selector);
        const raw = el?.textContent?.trim();

        if (!raw) return null;

        try {
            return JSON.parse(raw);
        } catch (error) {
            console.warn(`[CustomOptions] ${label} JSON invalid:`, error);
            return null;
        }
    }

    async renderProductInfo({ requestUrl, callback }) {
        this._swapAbort?.abort();
    
        const controller = new AbortController();
        this._swapAbort = controller;
    
        try {
            const response = await fetch(requestUrl, {
                signal: controller.signal
            });
    
            if (!response.ok) {
                throw new Error(`Fetch HTTP: ${response.status} ${response.statusText}`);
            }
    
            const responseText = await response.text();
            const html = new DOMParser().parseFromString(responseText, 'text/html');
    
            callback(html);
        } catch (error) {
            if (error?.name !== 'AbortError') {
                console.error(error);
            }
        } finally {
            if (this._swapAbort === controller) {
                this._swapAbort = null;
            }
        }
    }

    stateContains(state, requiredState) {
        return Object.entries(requiredState ?? {}).every(([key, value]) => {
            return state[key] === value;
        });
    }

    getStateSpecificity(state) {
        return Object.keys(state ?? {}).length;
    }

    swapToProduct(product, state) {
        const variantId = this.resolveVariantId(state, product.productId);
        const sectionId = this.getSectionId();
        const requestUrl = this.buildRequestUrlWithParams(product.url, variantId, sectionId);
        const callback = this.handleSwapProduct(product.url, variantId);

        this.renderProductInfo({ requestUrl, callback });
    }

    getSectionId() {
        const productInfo = this.closest('manmade-product-info') ?? document.querySelector('manmade-product-info');
        return productInfo?.dataset.originalSection || productInfo?.dataset.section || null;
    }

    buildRequestUrlWithParams(url, variantId, sectionId) {
        const nextUrl = new URL(url, window.location.origin);

        if (variantId) {
            nextUrl.searchParams.set('variant', variantId);
        }

        if (sectionId) {
            nextUrl.searchParams.set('section_id', sectionId);
        }

        return `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
    }

    updateURL(url, variantId) {
        window.history.replaceState(
            {},
            '',
            this.buildRequestUrlWithParams(url, variantId)
        );
    }
}

if (!customElements.get('manmade-custom-option')) {
    customElements.define('manmade-custom-option', ManmadeCustomOption);
}
